import {
  WalletProviderPriority,
  getDefaultClient,
  getWalletProviderRegistry,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';
import type {
  SuiWalletFeatures,
  WalletWithFeatures,
} from '@mysten/wallet-standard';
import {
  getWallets,
  isWalletWithRequiredFeatureSet,
} from '@mysten/wallet-standard';

import { registerSuiNetworkProviderBuilder } from '../../registerSuiNetworkProviderBuilder';
import { createWalletProviderFromSuiStandardWallet } from '../utils/createWalletProviderFromSuiStandardWallet';

export const SUI_WALLET_STANDARD_EXTENSION_KEY = 'suiWalletStandard';

/**
 * Adds the Sui Wallet Standard extension to the Dynamic client.
 *
 * This extension enables integration with wallets that implement the Sui Wallet Standard,
 * automatically detecting and registering compatible wallets for use with the Dynamic SDK.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 */
export const addSuiWalletStandardExtension = (
  client = getDefaultClient()
): void => {
  if (
    hasExtension({ extensionKey: SUI_WALLET_STANDARD_EXTENSION_KEY }, client)
  ) {
    return;
  }

  registerExtension(
    { extensionKey: SUI_WALLET_STANDARD_EXTENSION_KEY },
    client
  );

  registerSuiNetworkProviderBuilder(client);

  const walletProviderRegistry = getWalletProviderRegistry(client);

  const walletStandardWallets = getWallets()
    .get()
    .filter((wallet) =>
      isWalletWithRequiredFeatureSet(wallet, [
        'standard:events',
        'standard:connect',
        'sui:signPersonalMessage',
      ])
    ) as WalletWithFeatures<SuiWalletFeatures>[];

  walletStandardWallets.forEach((walletStandardWallet) => {
    const walletProvider = createWalletProviderFromSuiStandardWallet({
      dynamicClient: client,
      walletStandardWallet,
    });

    walletProviderRegistry.register({
      priority: WalletProviderPriority.WALLET_SELF_ANNOUNCEMENT_STANDARD,
      walletProvider,
    });
  });
};
